import { Box, Button, Container } from '@material-ui/core'
import { Grid, Typography } from '@mui/material'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import CommonDialog from 'src/components/common/CommonDialog'
import CommonStepper from 'src/components/common/commonStepper'
import CreateMoreBtn from 'src/components/common/createMoreBtn'
import DetailItem from 'src/components/common/detailItem'
import FilterSection from 'src/components/common/filterSection'
import AddEditLesson from 'src/components/lesson/AddEditLesson'
import useQueryTable from 'src/hooks/useQueryTable'
import { DeleteChapterById, GetChapters } from 'src/redux/actions'
import { courseDifficultyLevel } from 'src/redux/constants'
import { LocalTable } from 'src/ui/Table'
import { GetChapterTableColumns, StatusCell } from 'src/util/columns'

import AddEditChapter from '../components/chapter/AddEditChapter'
import BulkUploadChapter, { downloadSampleChapterCsv } from '../components/chapter/BulkUploadChapter'

const steps = [
  'Create Chapter',
  'Create Lesson',
];

const ChapterList = () => {
  const dispatch = useDispatch()
  const location = useLocation();
  const [recordToEdit, setRecordToEdit] = useState()
  const [customFilterQuery, setCustomFilterQuery] = useState()
  const [searchText, setSearchText] = useState(null)
  const [activeStep, setActiveStep] = useState(0);
  const [firstStepData, setFirstStepData] = useState(null);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState();
  const params = new URLSearchParams(location.search);
  const chapterId = params.get("_id");
  const courseId = params.get("courseId");

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const { data, isLoading, isFetching, refetch } = useQueryTable({
    name: 'chapters',
    searchText: searchText,
    pageIndex: pageIndex,
    pageSize: pageSize,
    additionalFilters: customFilterQuery,
  })

  // filter chapter by chapterId
  useEffect(() => {
    if (!chapterId) return;
    setCustomFilterQuery(prev => ({
      ...(prev || {}),
      _id: chapterId,
    }));
  }, [chapterId]);

  // filter chapter by courseId
  useEffect(() => {
    setCustomFilterQuery(prev => {
      const updatedQuery = { ...(prev || {}) };

      if (courseId) {
        updatedQuery.unit = courseId;
      } else {
        delete updatedQuery.unit;
      }

      return updatedQuery;
    });
  }, [courseId]);


  // set chapter data for add lesson by action button
  useEffect(() => {
    if (recordToEdit) {
      setFirstStepData({
        languageId: recordToEdit?.course?._id,
        courseId: recordToEdit?.unit?._id,
        chapterId: recordToEdit?._id,
        difficultyLevel: recordToEdit?.difficultyLevel,
      });
    }
  }, [recordToEdit]);


  const onDeleteRow = async recordToDelete => {
    if (recordToDelete) {
      await dispatch(DeleteChapterById(recordToDelete))
      refetch()
    }
  }

  const columns = useMemo(() => GetChapterTableColumns(), []);

  const actionTitle = Object.keys(recordToEdit || {}).length === 0 ? "create-chapter" : "";

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3,
        }}
      >
        <Container maxWidth={false}>

          {/* addButton filters & search section  */}
          <FilterSection
            type="Chapter"
            path="chapters"
            dispatcher={GetChapters}
            setSearchText={setSearchText}
            setRecordToEdit={setRecordToEdit}
            setCustomFilterQuery={setCustomFilterQuery}
            secondaryButton={
              <Button variant="outlined" color="primary" onClick={() => setIsBulkUploadOpen(true)}>
                + Bulk Upload
              </Button>
            }
          />

          {/* Chapter data List */}
          <Box sx={{ pt: 3 }}>
            <LocalTable
              columns={columns}
              toolbarExtra={
                <Button variant="text" size="small" onClick={downloadSampleChapterCsv}>
                  Download Sample CSV
                </Button>
              }
              onEditClick={setRecordToEdit}
              onDeleteClick={onDeleteRow}
              additionalFilters={customFilterQuery}
              data={data}
              name="Chapter"
              exportApiName="chapters"
              exportFileName="chapters.csv"
              isLoading={isLoading}
              isFetching={isFetching}
              setPagination={setPagination}
              addChildActionBtnTitle="Create Lesson"
              addChildActionBtnClick={() => {
                setActiveStep(1);
              }}
              viewChildActionBtnTitle="View lesson"
              viewChildActionBtnUri={`/app/lessons?chapterId=`}
              viewActionBtnTitle="View Chapter"
              onViewActionBtnClick={setViewRecord}
              editActionBtnTitle="Edit Chapter"
              deleteActionBtnTitle="Delete Chapter"
            />
          </Box>

          {/* View chapter details modal */}
          <CommonDialog
            open={!!viewRecord}
            title="Chapter Details"
            onClose={() => setViewRecord(null)}
            maxWidth="sm"
          >
            <Grid container spacing={2}>
              <DetailItem label="Language" value={viewRecord?.course?.name} />
              <DetailItem label="Course" value={viewRecord?.unit?.name} />
              <DetailItem label="Chapter Name" value={viewRecord?.name} />
              <DetailItem
                label="Difficulty Level"
                value={courseDifficultyLevel.find(d => d.key === viewRecord?.difficultyLevel)?.value}
              />
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                >
                  Description
                </Typography>
                <Typography variant="body1">{viewRecord?.description || 'N/A'}</Typography>
              </Grid>
              <DetailItem label="Sort Order" value={viewRecord?.sortOrder} />
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                >
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <StatusCell
                    value={
                      viewRecord?.isActive === false || viewRecord?.isActive === 'false'
                        ? 'InActive'
                        : 'Active'
                    }
                  />
                </Box>
              </Grid>
              <DetailItem
                label="Created On"
                value={viewRecord?.createdAt ? moment(viewRecord.createdAt).format('DD/MM/YYYY') : null}
              />
            </Grid>
          </CommonDialog>

          {/* Bulk upload chapters via CSV */}
          <CommonDialog
            open={isBulkUploadOpen}
            title="Bulk Upload Chapters"
            onClose={() => setIsBulkUploadOpen(false)}
            maxWidth="sm"
          >
            <BulkUploadChapter
              refetch={refetch}
              toggleDrawer={() => setIsBulkUploadOpen(false)}
            />
          </CommonDialog>

          {/* Add & Update Chapter & Add lesson modal*/}
          <CommonDialog
            open={!!recordToEdit}
            title={
              activeStep === 0
                ? `${actionTitle ? "Create" : "Update"} chapter`
                : "Create Lesson"
            }
            onClose={() => {
              setRecordToEdit();
              setFirstStepData(null);
              setActiveStep(0)
            }}
            maxWidth="md"
          >
            {/*Stepper Box */}
            {actionTitle && <CommonStepper steps={steps} activeStep={activeStep} />}

            <Box mt={2}>
              {/* Add & Update course form */}
              {activeStep === 0 && (
                <AddEditChapter
                  selectedRow={recordToEdit}
                  refetch={refetch}
                  toggleDrawer={() => {
                    setRecordToEdit();
                    setFirstStepData(null);
                    setActiveStep(0)
                  }}
                  setFirstStepData={setFirstStepData}
                  setActiveStep={setActiveStep}
                  actionTitle={actionTitle}
                />
              )}

              {/* Add & Update chapter form */}
              {activeStep === 1 && (
                <AddEditLesson
                  toggleDrawer={() => {
                    setRecordToEdit();
                    setFirstStepData(null);
                    setActiveStep(0)
                  }}
                  refetch={refetch}
                  firstStepData={firstStepData}
                  setActiveStep={setActiveStep}
                  actionTitle={actionTitle}
                />
              )}

              {/* Add practice success message & add new practice button */}
              {activeStep === 2 && (
                <CreateMoreBtn
                  notification="Lesson created successfully!"
                  createMoreBtnTitle="+ Create more Lesson"
                  onCreate={() => {
                    setActiveStep(1)
                  }}
                  onClose={() => {
                    setRecordToEdit()
                    setFirstStepData(null);
                    setActiveStep(0)
                  }}
                />
              )}

            </Box>
          </CommonDialog>

        </Container>
      </Box>
    </>
  )
}

export default ChapterList
